import { SUBSCRIPTION_STATUS } from '@adtrackify/at-tracking-event-types';
import { differenceInCalendarDays, isValid, parseISO, startOfDay } from 'date-fns';
import type { TrialInfo, EffectiveTrialState } from '../types/trial-indicator-types';

export function getTrialDaysRemaining(trialEndsOn: string | null | undefined): number | null {
  if (!trialEndsOn) {
    return null;
  }

  try {
    // Get current local time
    const todayStart = startOfDay(new Date());

    // Parse the trial end date
    // trial_ends_on can be "2026-01-08" (date only) or full ISO format with timezone
    let trialEndDate: Date;

    if (trialEndsOn.includes('T')) {
      // Full ISO format - timezone is embedded (e.g., "2026-01-12T11:12:08.000Z")
      const parsedDate = parseISO(trialEndsOn);
      if (!isValid(parsedDate)) {
        return null;
      }
      trialEndDate = parsedDate;
    } else {
      // Date only format like "2026-01-08"
      // Parse as midnight local time for calendar day comparison
      trialEndDate = parseISO(trialEndsOn);
      if (!isValid(trialEndDate)) {
        return null;
      }
    }

    const trialEndStart = startOfDay(trialEndDate);

    // Use differenceInCalendarDays for accurate day counting
    const daysRemaining = differenceInCalendarDays(trialEndStart, todayStart);

    return daysRemaining;
  } catch {
    console.error('Failed to parse trial end date:', trialEndsOn);
    return null;
  }
}

export function formatTrialRemaining(daysRemaining: number | null): string | null {
  if (daysRemaining === null || daysRemaining < 0) {
    return null;
  }

  if (daysRemaining === 0) {
    return 'Trial ends today';
  }

  if (daysRemaining === 1) {
    return 'Trial ends in 1 day';
  }

  return `Trial ends in ${daysRemaining} days`;
}

export function getTrialInfo(trialEndsOn: string | null | undefined): TrialInfo {
  const daysRemaining = getTrialDaysRemaining(trialEndsOn);
  const message = formatTrialRemaining(daysRemaining);
  const isActive = daysRemaining !== null && daysRemaining >= 0;

  return {
    daysRemaining,
    message,
    isActive,
  };
}

export function getEffectiveTrialState(
  trialEndsOn: string | null | undefined,
  status: SUBSCRIPTION_STATUS | undefined
): EffectiveTrialState {
  if (status === SUBSCRIPTION_STATUS.TRIALING) {
    const { daysRemaining, message } = getTrialInfo(trialEndsOn);
    return {
      isOnTrial: true,
      daysRemaining,
      message,
    };
  }

  if (status === SUBSCRIPTION_STATUS.ACTIVE) {
    const { daysRemaining, message, isActive: dateActive } = getTrialInfo(trialEndsOn);
    if (dateActive) {
      return {
        isOnTrial: true,
        daysRemaining,
        message,
      };
    }
  }

  return {
    isOnTrial: false,
    daysRemaining: null,
    message: null,
  };
}

